<svelte:head>

    <link rel="stylesheet" type="text/css" href="/global.css" />
    <link rel="stylesheet" type="text/css" href="/css/forms.css" />

    {#if !stripeInitialized}

        <script src="https://js.stripe.com/v3/" on:load={stripeLoaded} ></script>

    {/if}

</svelte:head>

<script>

    import { onMount } from "svelte";
    import { createEventDispatcher } from "svelte";
    import { fade } from 'svelte/transition';
    import dayjs from "dayjs";

    import PropertiesList from "../settings/locations/list.svelte";

    import {
        errorReasons,
        plansTiered,
        getCouponInfo,
        userProfile,
        getStripePublickey,
        customerCreate,
        customerUpdate,
        customerUpdateConfirm,
        getSubscription,
        checkSubscription,
        changeSubscription,
        cancelSubscription,
        listProperties,
        disableProperty
    } from "../api.svelte";


    const ERROR_DISAPPEAR_AFTER = 10000;

    const SUBSCRIPTION_STATUS_TEXTS = {
        'active': {
            'status': 'Aktiv',
            'secondary': 'Nächste Belastung'
        },
        'cancelled': {
            'status': 'Gekündigt',
            'secondary': 'Läuft aus'
        },
        'inactive': {
            'status': 'Inaktiv',
            'secondary': ''
        }
    };

    const PAYMENT_BUTTON_TEXT = {
        'active': 'Anpassen',
        'cancelled': 'Abo verlängern',
        'inactive': 'Neues Abo abschliessen',
        'pending': 'Jetzt testen'
    };

    const CHANGE_MODAL_TEXTS = {
        'uncancel': 'Möchten Sie Ihr Abo wieder starten?',
        'upgrade': 'Möchten Sie Ihr Abo anpassen?\r\n'
            + 'Ihnen stehen dann neu [@numProperties] Standort(e) zur Verfügung.',
        'downgrade': {
            'normal': 'Möchten Sie Ihr Abo anpassen?\r\n'
                    + 'Ihnen stehen dann neu [@numProperties] Standort(e) zur Verfügung.',
            'delete': 'Sie müssen dafür [@numDelete] Standort(e) löschen.'
        },
        'period': 'Möchten Sie Ihr Abo anpassen?',
        'propertieslist': 'Sie müssen noch [@numDelete] Standort(e) löschen.',
        'propertydelete': 'Wollen sie die Standort [@locationName] löschen?'
    };

    const COUPON_TEXTS = {
        'forever': 'nur einmal gültig'
    };


    const dispatch = createEventDispatcher();

    export let currency;
    export let tier;
    export let vatPercentage = 0;
    export let currentPeriod = 'monthly';
    export let currentNumProperties = 1;
    export let payButtonText = '';

    export let cardData = false;
    export let subscriptionData = false;
    export let isCardUpdate = false;
    export let isStatic = false;
    export let showLogo = true;
    export let showInlineCard = true;
    export let showCoupon = false;

    let plans;
    let paymentMeta;
    let email;
    let properties = false;
    let currentPropertyId = false;
    let couponText = '';
    let couponTimer = null;
    let couponStatus = 'none';
    let couponInfo = false;
    let stripe;
    let stripeReady = false;
    let stripeInitialized = false;
    let stripePublickey;
    let stripeCard;
    let paymentSuccessful = false;

    let cardContainer;
    let cardChangeElementInterval;

    let cardError = false;
    let isLoading = false;
    let resetTimer = null;

    let currentModal = '';

    $: planId = [tier, currentPeriod, currency].join('-');

    $: getSubtotal = () => {

        if (!plans) return 0.0;

        let amount = parseFloat(plans[currentPeriod]['amount']);

        return currentNumProperties * (amount);

    };

    $: getCoupon = () => {

        if (!plans || !couponInfo) return 0.0;

        let subtotal = getSubtotal();
        let couponAmount = subtotal * (couponInfo['percent_off'] / 100);

        return Math.ceil(couponAmount * 20) / 20;

    };

    $: getVat = () => {

        if (!plans) return 0.0;

        let subtotal = getSubtotal();

        if (couponStatus === 'success') {

            subtotal = subtotal - getCoupon();
        }

        let taxAmount = subtotal * (vatPercentage / 100);

        return Math.ceil(taxAmount * 20) / 20;

    };

    $: getTotal = () => {

        if (!plans) return 0.0;

        let subtotal = getSubtotal();

        if (couponStatus === 'success') {

            subtotal = subtotal - getCoupon();
        }

        let vat = getVat();

        return subtotal + vat;

    };

    $: isTerminated = () => {

        return subscriptionData['status'] !== 'active';
    };

    $: getPayButtonText = () => {

        return (paymentSuccessful) ? 'Zahlung erfolgreich'
            : PAYMENT_BUTTON_TEXT[subscriptionData['status']];

    };


    onMount(() => {

        plansTiered(currency, tier).then((plansResult) => {

            if (plansResult['status'] !== 'success') {

                // TODO: Errore handling I guess
                return;
            }

            plans = plansResult['plans'];

        });

        userProfile().then((profileResult) => {

            if (profileResult['status'] !== 'success') {

                // TODO: Errore handling I guess
                return;

            }

            email = profileResult['profile']['email'];

        });

        checkSubscription().then((checkResult) => {

            if (checkResult['status'] !== 'success') {

                // TODO: Errore handling I guess
                return;

            }

            paymentMeta = checkResult['payment'];

        });

        listProperties().then((listResult) => {

            if (listResult['status'] !== 'success') {

                // TODO: Handle errore
                console.log(listResult);

                return;

            }

            properties = listResult['properties'];

        });

    });

    let startResetTimer = () => {

        if (resetTimer) {

            clearTimeout(resetTimer);
        }

        resetTimer = setTimeout(() => {

            cardError = false;

        }, ERROR_DISAPPEAR_AFTER);

    };

    let switchPeriod = (selectedPeriod) => {

        if (isStatic) return;

        currentPeriod = selectedPeriod;

    };

    let stripeLoaded = () => {

        getStripePublickey().then((publickeyResult) => {

            if (publickeyResult['status'] !== 'success') {

                cardError = publickeyResult['reason'];
                startResetTimer();

                return;

            }

            stripePublickey = publickeyResult['publickey'];
            stripeReady = true;

            if (!isStatic) {

                stripeElements();
            }

        });

    };

    let stripeElements = (isChange = false) => {

        if (stripeInitialized) return;

        if (!cardContainer) return;

        if (cardChangeElementInterval) {

            clearInterval(cardChangeElementInterval);
        }

        stripeInitialized = true;

        stripe = Stripe(stripePublickey, {

            locale: 'de'

        });

        let elements = stripe.elements();

        let style = {
            base: {
                color: "#32325d",
                fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                fontSmoothing: "antialiased",
                fontSize: "16px",
                "::placeholder": {
                    color: "#aab7c4"
                }
            },
            invalid: {
                color: "#fa755a",
                iconColor: "#fa755a"
            }
        };

        stripeCard = elements.create('card', {style: style});

        stripeCard.mount(cardContainer);

        stripeCard.on('focus', () => {

            cardContainer.classList.add('focused');
        });

        stripeCard.on('blur', () => {

            cardContainer.classList.remove('focused');
        });

    };

    let createPaymentMethod = (create = true) => {

        stripe.createPaymentMethod('card', stripeCard, {

            billing_details: {
                email: email
            }

        }).then((result) => {

            if (result.error) {

                console.log('CardError:', result.error);

                isLoading = false;
                cardError = result.error;
                startResetTimer();

                return;

            }

            if (create) {

                createCustomer(result.paymentMethod.id);

            } else {

                updateCustomer(result.paymentMethod.id);

            }

        });

    };

    let createCustomer = (paymentMethod) => {

        let couponCode = (couponStatus === 'success') ? couponText : '';

        customerCreate(planId, paymentMethod, email, currentNumProperties, currency, couponCode)
            .then((customerResult) => {

                if (customerResult['status'] !== 'success') {

                    console.log('CustomerError:', customerResult['reason']);

                    isLoading = false;
                    cardError = customerResult['reason'];
                    startResetTimer();

                    return;

                }

                if (customerResult['subscription']['error']) {

                    console.log('SubscriptionError:', customerResult['subscription']['error']);

                    isLoading = false;
                    cardError = customerResult['subscription']['error'];
                    startResetTimer();

                    return;

                }

                handleSubscription(customerResult['subscription']);

            });

    };

    let updateCustomer = (paymentMethod) => {

        customerUpdate(paymentMethod).then((updateResult) => {

            if (updateResult['status'] !== 'success') {

                console.log('CustomerUpdateError:', updateResult['reason']);

                isLoading = false;
                cardError = updateResult['reason'];
                startResetTimer();

                return;

            }

            if (updateResult['intent']['error']) {

                console.log('CustomerUpdateError:', updateResult['intent']['error']);

                isLoading = false;
                cardError = updateResult['intent']['error'];
                startResetTimer();

                return;

            }

            handleCardUpdate(updateResult['intent']);

        });

    };

    let handleCardUpdate = (intent) => {

        const { client_secret, status } = intent;

        if (status === 'requires_action' || status === 'requires_payment_method') {

            stripe.confirmCardSetup(client_secret).then((result) => {

                console.log('Result:', result);

                if (result.error) {

                    isLoading = false;
                    cardError = result.error;
                    startResetTimer();

                    return;

                }

                confirmCardUpdate(result.setupIntent.payment_method)

            });

        } else {

            confirmCardUpdate(intent.payment_method);

        }

    };

    let confirmCardUpdate = (paymentMethod) => {

        customerUpdateConfirm(paymentMethod).then((confirmResult) => {

            if (confirmResult['status'] !== 'success') {

                console.log('CustomerUpdateConfirmError:', confirmResult['reason']);

                isLoading = false;
                cardError = confirmResult['reason'];
                startResetTimer();

                return;

            }

            dispatch('cardUpdated');

        });

    };

    let handleSubscription = (subscription) => {

        const { latest_invoice } = subscription;
        const { payment_intent } = latest_invoice;

        if (payment_intent) {

            const { client_secret, status } = payment_intent;

            if (status === 'requires_action' || status === 'requires_payment_method') {

                stripe.confirmCardPayment(client_secret).then((result) => {

                    if (result.error) {

                        console.log('ConfirmError:', result.error);

                        isLoading = false;
                        cardError = result.error;
                        startResetTimer();

                        return;

                    }

                    confirmSubscription(subscription.id);

                });

                return;

            }

            onOrderComplete(subscription);
            return;

        }

        onOrderComplete(subscription);

    };

    let confirmSubscription = (subscriptionId) => {

        getSubscription(subscriptionId).then((subscriptionResult) => {

            if (subscriptionResult['status'] !== 'success') {

                console.log('ConfirmError:', subscriptionResult['status']);

                isLoading = false;
                cardError = subscriptionResult['reason'];
                startResetTimer();

                return;

            }

            onOrderComplete(subscriptionResult['subscription']);

        });

    };

    let onOrderComplete = (subscription) => {

        console.log('orderComplete', subscription);

        isLoading = false;
        paymentSuccessful = true;

        dispatch('complete');

    };

    let changeConfirm = () => {

        isLoading = true;

        changeSubscription(planId, currentNumProperties).then((changeResult) => {

            isLoading = false;
            currentModal = '';

            if (changeResult['status'] !== 'success') {

                console.log('CustomerError:', changeResult['reason']);

                cardError = changeResult['reason'];
                startResetTimer();

                return;

            }

            if (changeResult['subscription']['error']) {

                console.log('SubscriptionError:', changeResult['subscription']['error']);

                cardError = changeResult['subscription']['error'];
                startResetTimer();

                return;

            }

            // TODO: check if requires action - see handleSubscription

            dispatch('subscriptionUpdated');

        });

    };

    let paymentHandler = () => {

        if (isLoading || paymentSuccessful || isStatic) return;

        isLoading = true;

        createPaymentMethod();

    };

    let updatePaymentMethod = () => {

        if (isLoading || paymentSuccessful) return;

        isLoading = true;

        createPaymentMethod(false);

    };

    const getSubscriptionStatus = () => {

        return SUBSCRIPTION_STATUS_TEXTS[subscriptionData['status']]['status'];
    };

    const getSubscriptionMeta = () => {

        return SUBSCRIPTION_STATUS_TEXTS[subscriptionData['status']]['secondary'];
    };

    const getSubscriptionExp = () => {

        if (subscriptionData['status'] === 'inactive') return '';

        return dayjs.unix(subscriptionData['expiration']).format('DD.MM.YY');

    };

    let onModalCancel = () => {

        if (isLoading) return;

        currentModal = '';

    };

    let handleCouponChanged = () => {

        if (couponTimer) {

            clearTimeout(couponTimer);
            couponTimer = null;

        }

        if (couponText === '' || couponText.length < 4) {

            couponStatus = 'none';
            return;

        }

        couponStatus = 'none';

        couponTimer = setTimeout(() => {

            clearTimeout(couponTimer);
            couponTimer = null;

            handleCouponInfo();

        }, 1000);

    };

    let handleCouponInfo = () => {

        getCouponInfo(couponText).then((couponResult) => {

            if (couponResult['status'] !== 'success') {

                if (couponResult['reason'] === 'coupon_notfound') {

                    // TODO: Errore
                    couponStatus = 'failed';

                    return;

                }

                if (couponResult['reason'] === 'coupon_invalid') {

                    // TODO: Errore
                    couponStatus = 'failed';
                    console.log(couponResult);

                    return;

                }

            }

            couponInfo = couponResult['coupon'];
            couponStatus = 'success';

        });

    };

    let onTerminateClicked = () => {

        currentModal = 'terminate';
    };

    let onTerminateConfirmed = () => {

        isLoading = true;

        cancelSubscription().then((cancelResult) => {

            isLoading = false;

            if (cancelResult['status'] !== 'success') {

                // TODO: Handle errore
                console.log(cancelResult);

                return;

            }

            dispatch('subscriptionTerminated');
            currentModal = '';

        });

    };

    let onEditPlanClicked = () => {

        dispatch('editPlan');
    };

    let onEditCardClicked = () => {

        dispatch('editCard');

        cardChangeElementInterval = setInterval(() => {

            stripeElements(true);

        }, 100);

    };

    let onSubscriptionChange = () => {

        // No change, just uncancel
        if (currentNumProperties === paymentMeta['paid']
            && currentPeriod === paymentMeta['planid'].split('-')[1]) {

            if (paymentMeta['status'] === 'cancelled') {

                currentModal = 'uncancel';
                return;

            }

            return;

        }

        // Upgrade numLocations
        if (currentNumProperties > paymentMeta['paid']) {

            currentModal = 'upgrade';
            return;

        }

        // Downgrade numLocations
        if (currentNumProperties < paymentMeta['paid']) {

            currentModal = 'downgrade';
            return;

        }

        currentModal = 'period';

    };

    let onDowngrade = () => {

        // No need to delete a location
        if (currentNumProperties >= paymentMeta['used']) {

            changeConfirm();
            return;

        }

        currentModal = 'propertieslist';

    };

    let onDeleteCancel = () => {

        currentModal = 'propertieslist';
    };

    let onDeleteProperty = (evt) => {

        currentPropertyId = evt.detail['propertyid'];
        currentModal = 'propertydelete';

    };

    let onDeletePropertyConfirm = () => {

        isLoading = true;

        disableProperty(currentPropertyId).then((disableResult) => {

            if (disableResult['status'] !== 'success') {

                isLoading = false;

                // TODO: Handle errore
                console.log(disableResult);

                return;

            }

            currentPropertyId = false;
            changeConfirm();

        });

    };

</script>


{#if !isCardUpdate}

    <div id="payment-container" class="forms-container">

        {#if showLogo}

            <img id="logo" src="/images/logo.svg" alt="keyosh Logo" />

        {/if}

        <form on:submit|preventDefault={() => paymentHandler()} id="paymentform" class="forms">

            <div id="payment-period">

                <button
                        class={'btn ' + ((currentPeriod === 'monthly') ? 'active' : '')}
                        on:click|preventDefault={() => switchPeriod('monthly')}>

                    <span>Monatlich</span>

                </button>

                <button
                        class={'btn ' + ((currentPeriod === 'yearly') ? 'active' : '')}
                        on:click|preventDefault={() => switchPeriod('yearly')}>

                    <span>Jährlich (-15%)</span>

                </button>

            </div>

            <table>

                <tbody>

                <tr class="bordered-bottom">

                    <td colspan="2">

                        <span class="billing-section-title">

                            Anzahl Standorte <span class="billing-number">{currentNumProperties}</span>

                        </span>

                        <div class="location-slider">

                            <input
                                    type="range" min="1" max="5"
                                    bind:value={currentNumProperties}
                                    disabled={isStatic} />

                        </div>

                    </td>

                </tr>

                <tr>

                    <td>

                        <span class="billing-title">Subtotal</span>

                    </td>

                    <td>

                        <span id="subtotal">{getSubtotal().toFixed(2)}</span>

                    </td>

                </tr>

                {#if couponStatus === 'success' && couponInfo}

                    <tr>

                        <td id="coupon-meta">

                            <span class="billing-title">Coupon ({couponInfo['percent_off'].toFixed(1)}%)</span>

                            <span id="coupon-description">

                                {COUPON_TEXTS[couponInfo['duration']]}

                            </span>

                        </td>

                        <td>

                            <span id="couponamt">- {getCoupon().toFixed(2)}</span>

                        </td>

                    </tr>

                {/if}

                <tr class="bordered-bottom">

                    <td>

                        <span class="billing-title">MWST ({vatPercentage.toFixed(1)}%)</span>

                    </td>

                    <td>

                        <span id="vat">{getVat().toFixed(2)}</span>

                    </td>

                </tr>

                <tr>

                    <td>

                        <span class="billing-title">Total</span>

                    </td>

                    <td>

                        <span id="total">

                            <span>{currency.toUpperCase()}</span> <span>{getTotal().toFixed(2)}</span>

                        </span>

                        <span id="period-info">

                            {(currentPeriod === 'monthly') ? 'monatlich' : 'jährlich'} abgerechnet

                        </span>

                    </td>

                </tr>

                </tbody>

            </table>

            {#if showCoupon}

                <div id="coupon">

                    <input
                            bind:value={couponText}
                            on:input={() => handleCouponChanged()}
                            class="{(couponStatus !== 'none') ? couponStatus : ''}"
                            name="coupon"
                            type="text"
                            placeholder="Coupon Code" />

                </div>

            {/if}

            {#if showInlineCard}

                <div id="payment-action" class={(paymentSuccessful) ? 'success' : ''}>

                    <div id="card-element" bind:this={cardContainer}></div>

                    {#if cardError}

                        <div transition:fade id="card-errors">

                            {(Object.keys(errorReasons).includes(cardError))
                                ? errorReasons[cardError] : (cardError.message || cardError)}

                        </div>

                    {/if}

                    <div class="notice-text">
                        Ihre Kreditkarte wird erst nach Ablauf der Testfrist belastet.
                        Sie können jederzeit kündigen.
                    </div>

                    <button class={(isLoading) ? 'loading btn' : 'btn'}>

                        <span>{getPayButtonText()}</span>
                        <img src="/images/button-loader.svg" />

                    </button>

                </div>

            {/if}

            {#if isStatic}

                <div class="manage-buttons">

                    {#if (!isTerminated())}

                        <button
                                class="btn danger half"
                                on:click|stopPropagation={() => onTerminateClicked()} >

                            <span>Kündigen</span>

                        </button>

                    {/if}

                    <button
                            class="btn {(!isTerminated()) ? 'half' : ''}"
                            on:click|stopPropagation={() => onEditPlanClicked()} >

                        <span>{getPayButtonText()}</span>

                    </button>

                </div>

            {/if}

            {#if (!isStatic) && (!showInlineCard)}

                <div id="payment-update-button">

                    <button
                            class={(isLoading) ? 'loading btn' : 'btn'}
                            on:click|preventDefault={() => onSubscriptionChange()} >

                        <span>{getPayButtonText()}</span>
                        <img src="/images/button-loader.svg" />

                    </button>

                </div>

            {/if}

        </form>

    </div>

{/if}

{#if (cardData && isStatic) || isCardUpdate}

    <div id="paymentmeta-container" class={(isCardUpdate) ? 'edit' : ''}>

        {#if !isCardUpdate}

            <div id="status-container" class="forms-container">

                <div class="billing-section-title">Abo Status</div>

                <div id="payment-status">
                    <span class="highlight">{getSubscriptionStatus()}</span>. {getSubscriptionMeta()}
                    <span class="highlight">{getSubscriptionExp()}</span>.
                </div>

            </div>

        {/if}

        <div id="card-container" class="forms-container">

            {#if !isCardUpdate}

                <div class="billing-section-title">Zahlungsmittel</div>

                <div id="card-display">

                    <div id="card-identifier">

                        <img src="/images/icons/creditcard.svg" />

                        <span>{cardData['brand'].toUpperCase()} {cardData['number']}</span>

                    </div>

                    <div id="card-date">{cardData['exp']['month']} / {cardData['exp']['year']}</div>

                </div>

                <div id="card-buttons">

                    <button class="btn" on:click={() => onEditCardClicked()}>

                        <span>Anpassen</span>

                    </button>

                </div>

            {:else}

                <div class="billing-section-title">Zahlungsmittel ändern</div>

                <div id="card-change-element" bind:this={cardContainer}></div>

                {#if cardError}

                    <div transition:fade id="card-change-errors">

                        {(Object.keys(errorReasons).includes(cardError))
                            ? errorReasons[cardError] : (cardError.message || cardError)}

                    </div>

                {/if}

                <div id="card-change-buttons">

                    <button
                            class={(isLoading) ? 'loading btn' : 'btn'}
                            on:click={() => updatePaymentMethod()}>

                        <span>Speichern</span>
                        <img src="/images/button-loader.svg" />

                    </button>

                </div>

            {/if}

        </div>

    </div>

{/if}

{#if currentModal !== ''}

    <div class="modal-bg" on:click={() => onModalCancel()}></div>

{/if}

{#if currentModal === 'terminate'}

    <div class="forms-container modal">

        <div class="modal-title">
            Möchten Sie Ihr Abo wirklich kündigen?
        </div>

        <div class="modal-buttons">

            <button
                    class="btn half"
                    on:click|preventDefault={() => onModalCancel()} >

                <span>Abbrechen</span>

            </button>

            <button
                    class="{(isLoading) ? 'loading btn' : 'btn'} danger half"
                    on:click|preventDefault={() => onTerminateConfirmed()} >

                <span>Bestätigen</span>
                <img src="/images/button-loader.svg" />

            </button>

        </div>

    </div>

{/if}

{#if currentModal === 'uncancel'}

    <div class="forms-container modal">

        <div class="modal-title">
            {CHANGE_MODAL_TEXTS['uncancel']}
        </div>

        <div class="modal-buttons">

            <button
                    class="btn half"
                    on:click|preventDefault={() => onModalCancel()} >

                <span>Abbrechen</span>

            </button>

            <button
                    class="{(isLoading) ? 'loading btn' : 'btn'} half"
                    on:click|preventDefault={() => changeConfirm()} >

                <span>Bestätigen</span>
                <img src="/images/button-loader.svg" />

            </button>

        </div>

    </div>

{/if}

{#if currentModal === 'upgrade'}

    <div class="forms-container modal">

        <div class="modal-title">

            {CHANGE_MODAL_TEXTS['upgrade']
                .replace('[@numProperties]', currentNumProperties.toString())}

        </div>

        <div class="modal-buttons">

            <button
                    class="btn half"
                    on:click|preventDefault={() => onModalCancel()} >

                <span>Abbrechen</span>

            </button>

            <button
                    class="{(isLoading) ? 'loading btn' : 'btn'} half"
                    on:click|preventDefault={() => changeConfirm()} >

                <span>Bestätigen</span>
                <img src="/images/button-loader.svg" />

            </button>

        </div>

    </div>

{/if}

{#if currentModal === 'downgrade'}

    <div class="forms-container modal">

        <div class="modal-title">

            {CHANGE_MODAL_TEXTS['downgrade']['normal']
                .replace('[@numProperties]', currentNumProperties.toString())}

            {#if (currentNumProperties < paymentMeta['used'])}

                {CHANGE_MODAL_TEXTS['downgrade']['delete']
                    .replace('[@numDelete]', (paymentMeta['used'] - currentNumProperties).toString())}

            {/if}
        </div>

        <div class="modal-buttons">

            <button
                    class="btn half"
                    on:click|preventDefault={() => onModalCancel()} >

                <span>Abbrechen</span>

            </button>

            <button
                    class="{(isLoading) ? 'loading btn' : 'btn'} half"
                    on:click|preventDefault={() => onDowngrade()} >

                <span>Bestätigen</span>
                <img src="/images/button-loader.svg" />

            </button>

        </div>

    </div>

{/if}

{#if currentModal === 'period'}

    <div class="forms-container modal">

        <div class="modal-title">
            {CHANGE_MODAL_TEXTS['period']}
        </div>

        <div class="modal-buttons">

            <button
                    class="btn half"
                    on:click|preventDefault={() => onModalCancel()} >

                <span>Abbrechen</span>

            </button>

            <button
                    class="{(isLoading) ? 'loading btn' : 'btn'} half"
                    on:click|preventDefault={() => changeConfirm()} >

                <span>Bestätigen</span>
                <img src="/images/button-loader.svg" />

            </button>

        </div>

    </div>

{/if}

{#if currentModal === 'propertieslist'}

    <div class="forms-container modal">

        <div class="modal-title">

            {CHANGE_MODAL_TEXTS['propertieslist']
                .replace('[@numDelete]', (paymentMeta['used'] - currentNumProperties).toString())}

        </div>

        <div id="properties-list">

            <PropertiesList
                    properties={properties}
                    numAvailable={0}
                    showFormtitle={false}
                    on:preview={onDeleteProperty} />

        </div>

    </div>

{/if}

{#if currentModal === 'propertydelete'}

    <div class="forms-container modal">

        <div class="modal-title">

            {#if properties[currentPropertyId]}

                {CHANGE_MODAL_TEXTS['propertydelete']
                    .replace('[@locationName]', properties[currentPropertyId]['details']['name'])}

            {/if}

        </div>

        <div class="modal-buttons">

            <button
                    class="btn half"
                    on:click|preventDefault={() => onDeleteCancel()} >

                <span>Abbrechen</span>

            </button>

            <button
                    class="{(isLoading) ? 'loading btn' : 'btn'} half"
                    on:click|preventDefault={() => onDeletePropertyConfirm()} >

                <span>Bestätigen</span>
                <img src="/images/button-loader.svg" />

            </button>

        </div>

    </div>

{/if}


<style>

    img#logo {
        height: 4.26vh;
        margin-bottom: 4vh;
    }

    #payment-period {
        display: flex;
        flex-direction: row;
        justify-content: stretch;
        margin-bottom: 2.5vh;
    }

    #payment-period button,
    #card-buttons button,
    #card-change-buttons button {
        width: 17.22vh;
        padding: 1.42vh;
        font-size: 1.78vh;
    }

    #payment-period button:not(.active) {
        font-weight: 400;
        color: #383874;
        background-color: #ffffff;
    }

    #payment-period button:first-child {
        border-radius: 1vh 0 0 1vh;
    }

    #payment-period button:last-child {
        border-radius: 0 1vh 1vh 0;
    }

    table {
        font-family: 'Raleway', sans-serif;
        font-size: 1.66vh;
        border-collapse: collapse;
    }

    table tr.bordered-bottom {
        border-bottom: 1px solid rgba(56, 56, 116, 0.2);
    }

    table tr td {
        padding-top: 0.88vh;
        padding-bottom: 0.88vh;
    }

    table tr td:nth-child(1) {
        text-align: left;
    }

    table tr td:nth-child(2) {
        text-align: right;
        font-size: 1.77vh;
    }

    .billing-section-title {
        font-weight: 400;
        font-size: 1.85vh;
        text-transform: uppercase;
    }

    .billing-title {
        font-weight: 300;
        font-size: 1.66vh;
        text-transform: uppercase;
    }

    .billing-number {
        display: inline-flex;
        width: 2.13vh;
        height: 2.13vh;
        margin-left: 0.88vh;
        text-align: center;
        justify-content: center;
        flex-direction: row;
        font-family: 'Roboto', sans-serif;
        font-weight: 500;
        font-size: 1.6vh;
        line-height: 2.2vh;
        background-color: #22cde8;
        border-radius: 100%;
        color: white;
    }

    /*#coupon-meta span {
        display: block;
    }*/

    #subtotal, #vat, #couponamt {
        font-family: 'Roboto', sans-serif;
        font-weight: 300;
        font-size: 1.66vh;
    }

    #total {
        font-family: 'Roboto', sans-serif;
        font-weight: 500;
    }

    #coupon-description, #period-info {
        display: block;
        font-size: 1.42vh;
    }

    .location-slider {
        width: 29.72vh;
        padding-top: 2.66vh;
        padding-bottom: 2.66vh;
    }

    .location-slider input {
        -webkit-appearance: none;
        appearance: none;
        width: 90%;
        padding: 0 5%;
        height: 10px;
        background-color: #22CDE8;
        border-radius: 8px;
        outline: none;
    }

    .location-slider input::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 3vh;
        height: 3vh;
        background-image: url(/images/icons/sliderthumb.svg);
        background-size: contain;
        background-repeat: no-repeat;
        cursor: pointer;
    }

    .location-slider input::-moz-range-thumb {
        width: 3vh;
        height: 3vh;
        background-image: url(/images/icons/sliderthumb.svg);
        background-size: contain;
        background-repeat: no-repeat;
        cursor: pointer;
    }

    #coupon {
        margin-top: 1.5vh;
    }

    #coupon input {
        width: 26vh;
        padding: 1.2vh;
        font-size: 1.5vh;
        border: 1px solid transparent;
    }

    #coupon input.success {
        border: 1px solid #41e03c;
    }

    #coupon input.failed {
        border: 1px solid #f44336;
    }

    #payment-action {
        margin-top: 4vh;
    }

    #card-element,
    #card-change-element {
        padding: 1.2vh;
        margin-bottom: 2.22vh;
        border-radius: 1vh;
        -webkit-box-shadow: 0px 10px 30px 0px rgb(73 119 172 / 15%);
        -moz-box-shadow: 0px 10px 30px 0px rgb(73 119 172 / 15%);
        box-shadow: 0px 10px 30px 0px rgb(73 119 172 / 15%);
    }

    #card-change-element {
        width: 30vh;
        margin-top: 3.14vh;
    }

    #card-errors,
    #card-change-errors {
        width: 31vh;
        padding: 1.78vh;
        margin-bottom: 2.67vh;
        font-size: 1.8vh;
    }

    .notice-text {
        max-width: 34.62vh;
        margin-bottom: 2.03vh;
        font-size: 1.48vh;
    }

    #payment-action button,
    #payment-update-button button {
        width: 100%;
        padding: 1.42vh;
        font-size: 1.78vh;
    }

    #payment-update-button button {
        margin-top: 2vh;
    }

    .manage-buttons {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        margin-top: 4.16vh;
    }

    .manage-buttons button.btn:not(.half) {
        width: 100%;
    }

    #paymentmeta-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        margin-left: 7.22vh;
        font-family: 'Raleway', sans-serif;
    }

    #paymentmeta-container.edit {
        margin-left: 0;
    }

    #status-container {
        margin-bottom: 5vh;
    }

    #payment-status {
        width: 30vh;
        margin-top: 3.14vh;
        font-size: 1.8vh;
    }

    #payment-status .highlight {
        font-weight: 500;
    }

    #card-container {}

    #card-display {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        min-width: 30vh;
        margin-top: 3.14vh;
        font-weight: 500;
        font-size: 2vh;
    }

    #card-identifier {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
    }

    #card-identifier img {
        margin-right: 1.48vh;
    }

    #card-date {
        margin-left: 2vh;
    }

    #card-buttons,
    #card-change-buttons {
        display: flex;
        flex-direction: row;
        justify-content: flex-end;
        align-items: center;
        margin-top: 3.7vh;
    }

    @media screen and (max-width: 1024px) {

        #payment-container {
            margin-top: 8vw;
            margin-bottom: 16vw;
        }

    }

</style>
